import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, Clock, CheckCircle2, ChevronLeft, Loader2, Play, CheckSquare, Square, Settings, RefreshCw, Filter, ArrowLeft, X, Download, Upload } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchAnswerSheets, evaluateAnswerCopies, fetchEvaluationStatus, setCurrentAnswerSheetPage, downloadCsv } from "./EvaluatorSlice";
import { resetUpload } from "../scanning/ScanningSlice";
import { PdfPreviewModal } from "../scanning/preview/PdfPreviewModal";
import { AnswerSheet } from "./types";
import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface AnswerSheetListProps {
    totalCopies: number;
    paperId: string;
    paperCode: string;
    subjectName: string;
    onViewDetails: (sheetId: string) => void;
    onSettingsClick: () => void;
}

const AnswerSheetList: React.FC<AnswerSheetListProps> = ({ totalCopies, paperId, paperCode, subjectName, onViewDetails, onSettingsClick }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { answerSheets, loading, evaluating, answerSheetsTotal, currentAnswerSheetPage } = useAppSelector((state: any) => state.evaluator);

    // Use answerSheetsTotal from API response, fallback to prop
    const actualTotalCopies = answerSheetsTotal > 0 ? answerSheetsTotal : totalCopies;

    // Pagination
    const currentPage = currentAnswerSheetPage || 1;
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Selection
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isEvaluationPending, setIsEvaluationPending] = useState(false);

    // Filtering
    const [filterStatus, setFilterStatus] = useState<'all' | 'processing' | 'completed' | 'failed'>('all');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    useEffect(() => {
        if (paperId) {
            const offset = (currentPage - 1) * itemsPerPage;
            dispatch(fetchAnswerSheets({
                paperId,
                limit: itemsPerPage,
                offset,
                status: filterStatus === 'all' ? undefined : filterStatus
            }));
            // Check evaluation status on mount/update
            dispatch(fetchEvaluationStatus(paperId)).unwrap().then((result: any) => {
                setIsEvaluationPending(result.evaluation_pending);
            }).catch(console.error);
        }
    }, [dispatch, paperId, itemsPerPage, currentPage, filterStatus]);

    // Derived values
    const totalPages = Math.ceil(actualTotalCopies / itemsPerPage);
    const safeTotalCopies = Math.max(0, actualTotalCopies);
    const startIndex = (currentPage - 1) * itemsPerPage;
    // With server-side filtering/pagination, the answerSheets are already the current items
    // However, since we heavily rely on client-side pagination structures here implying
    // we only got one page, we just show what we have.
    const currentItems = answerSheets;
    const isProcessing = (status: string | null) => status === 'processing' || status === 'in-progress';
    const selectableItems = currentItems.filter((item: AnswerSheet) => !isProcessing(item.status));
    const isAllVisibleSelected = selectableItems.length > 0 && selectableItems.every((item: { id: string; }) => selectedIds.includes(item.id));
    const hasProcessingSheets = currentItems.some((item: AnswerSheet) => isProcessing(item.status));

    // Pagination Handlers
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) dispatch(setCurrentAnswerSheetPage(newPage));
    };



    // Tooltip State
    const [showRefreshTooltip, setShowRefreshTooltip] = useState(false);

    // Selection Handlers
    const handleSelectAll = () => {
        const selectableItems = currentItems.filter((item: AnswerSheet) => !isProcessing(item.status));
        const selectableIds = selectableItems.map((item: AnswerSheet) => item.id);

        // If there are no selectable items, do nothing
        if (selectableIds.length === 0) return;

        const allSelectableSelected = selectableIds.every((id: string) => selectedIds.includes(id));

        if (allSelectableSelected) {
            setSelectedIds(prev => prev.filter(id => !selectableIds.includes(id)));
        } else {
            setSelectedIds(prev => [...new Set([...prev, ...selectableIds])]);
        }
    };

    const handleSelectOne = (id: string) => {
        const item = currentItems.find((i: AnswerSheet) => i.id === id);
        if (item && isProcessing(item.status)) return;

        setSelectedIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
    };

    // Bulk Action
    const handleBulkEvaluate = async () => {
        if (selectedIds.length === 0) return;
        try {
            // Show tooltip after 2 seconds
            setTimeout(() => {
                setShowRefreshTooltip(true);
            }, 2000);

            setIsEvaluationPending(true); // Optimistically hide rules button
            await dispatch(evaluateAnswerCopies({ paperCodeId: paperId, fileIds: selectedIds })).unwrap();
            const offset = (currentPage - 1) * itemsPerPage;
            dispatch(fetchAnswerSheets({
                paperId,
                limit: itemsPerPage,
                offset,
                status: filterStatus === 'all' ? undefined : filterStatus
            }));
            setSelectedIds([]);
        } catch (error: any) {
            console.error("Evaluation failed", error);
            setIsEvaluationPending(false); // Roll back optimistic update
            const message = typeof error === 'string' ? error : error?.message || "Failed to start evaluation. Please try again.";
            toast.error(message);
        }
    };

    const handleRefresh = () => {
        const offset = (currentPage - 1) * itemsPerPage;
        dispatch(fetchAnswerSheets({
            paperId,
            limit: itemsPerPage,
            offset,
            status: filterStatus === 'all' ? undefined : filterStatus
        }));
        setShowRefreshTooltip(false);
    };

    const handleRulesClick = async () => {
        onSettingsClick();
    };

    // CSV Download State
    const [downloading, setDownloading] = useState(false);

    const handleDownloadCsv = async () => {
        try {
            setDownloading(true);
            const result = await dispatch(downloadCsv(paperId)).unwrap();
            if (result.csv_url) {
                window.open(result.csv_url, '_blank');
                toast.success("CSV download started");
            } else {
                toast.error("No download URL received from server");
            }
        } catch (error: any) {
            const message = typeof error === 'string' ? error : error?.message || "Failed to download CSV. Please try again.";
            toast.error(message);
        } finally {
            setDownloading(false);
        }
    };

    // PDF View State
    const [viewPdfUrl, setViewPdfUrl] = useState<string | null>(null);

    const handleViewPdf = (e: React.MouseEvent, url: string) => {
        e.stopPropagation();
        setViewPdfUrl(url);
    };

    // Add Sheet (re-upload) State — reuses the same preview + presigned-upload flow as the first-time upload
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [filesToPreview, setFilesToPreview] = useState<File[]>([]);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    // Prefer the exact backend strings from a loaded sheet; fall back to the paper props.
    const firstSheet = answerSheets[0] as AnswerSheet | undefined;
    const uploadSubjectName = (firstSheet?.subjectName || subjectName || "").toLowerCase().trim();
    const uploadPaperCode = (firstSheet?.paperCode || paperCode || "").trim();

    const handleAddSheetsClick = () => {
        fileInputRef.current?.click();
    };

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;

        if (!uploadSubjectName || !uploadPaperCode) {
            toast.error("Could not determine subject / paper code for this paper.");
            e.target.value = "";
            return;
        }

        setFilesToPreview(Array.from(fileList));
        setIsPreviewOpen(true);

        // Allow re-selecting the same file later
        e.target.value = "";
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        setFilesToPreview([]);
        dispatch(resetUpload());
    };

    const handleSheetsUploaded = () => {
        toast.success("Answer sheet(s) added successfully");
        // Refresh the list so the new sheets appear
        const offset = (currentPage - 1) * itemsPerPage;
        dispatch(fetchAnswerSheets({
            paperId,
            limit: itemsPerPage,
            offset,
            status: filterStatus === 'all' ? undefined : filterStatus
        }));
    };

    return (
        <div className="flex-1 p-2 pt-2 md:p-4 bg-gray-50/30 flex flex-col relative">
            {/* Header Control Bar */}
            <Button
                variant="outline"
                onClick={() => navigate('/evaluator')}
                className="flex items-center gap-2 mb-4 text-sm font-medium text-gray-500 hover:text-gray-900 w-fit px-3 py-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-all hover:shadow-sm"
            >
                <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </Button>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                <div className="flex flex-col gap-1 shrink-0">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-3 whitespace-nowrap">
                        Answer Sheets
                        <span className="bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full text-xs font-mono">
                            {safeTotalCopies} Total
                        </span>
                    </h3>
                    <p className="text-sm text-gray-500">Select sheets to evaluate.</p>
                </div>

                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full sm:w-auto">
                    {/* Hidden file input for re-uploading answer sheets */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        multiple
                        className="hidden"
                        onChange={handleFilesSelected}
                    />

                    {/* Add Sheet Button — preview + re-upload PDFs into this same paper */}
                    <button
                        onClick={handleAddSheetsClick}
                        title="Add answer sheet(s) to this paper"
                        className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-bold rounded-xl border border-border hover:bg-gray-50 hover:border-gray-300 transition-all flex-1 sm:flex-none justify-center"
                    >
                        <Upload className="w-4 h-4" />
                        <span className="sm:inline">Add Sheet</span>
                    </button>

                    {/* Evaluate Button */}
                    <button
                        onClick={handleBulkEvaluate}
                        disabled={evaluating || selectedIds.length === 0}
                        className="tour-list-evaluate flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-bold rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex-1 sm:flex-none justify-center"
                    >
                        {evaluating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
                        <span className="sm:inline">{evaluating ? "Evaluating..." : selectedIds.length > 0 ? `Evaluate (${selectedIds.length})` : "Evaluate"}</span>
                    </button>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <div className="relative flex-1 sm:flex-none">
                            {showRefreshTooltip && (
                                <div className="absolute bottom-full left-0 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg shadow-xl whitespace-nowrap animate-in fade-in slide-in-from-bottom-2 z-50">
                                    Refresh to see updates
                                    <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                            )}
                            <Button
                                onClick={handleRefresh}
                                variant="outline"
                                size="icon"
                                className="tour-list-refresh w-full sm:w-9"
                                title="Refresh List"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                            </Button>
                        </div>

                        {!isEvaluationPending && (
                            <Button
                                onClick={handleRulesClick}
                                variant="outline"
                                size="icon"
                                className="tour-list-settings flex-1 sm:flex-none sm:w-9"
                                title="Update Rules"
                            >
                                <Settings className="w-4 h-4" />
                            </Button>
                        )}

                        <Button
                            onClick={handleDownloadCsv}
                            disabled={downloading || isEvaluationPending || hasProcessingSheets}
                            variant="outline"
                            className="gap-2 px-3 flex-1 sm:flex-none"
                            title={isEvaluationPending || hasProcessingSheets ? "Export disabled while evaluating" : "Download CSV"}
                        >
                            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            <span className="hidden lg:inline">{downloading ? "Downloading..." : "Export CSV"}</span>
                        </Button>

                        <div className="relative flex-1 sm:flex-none">
                            <Button
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                variant="outline"
                                size="icon"
                                className="tour-list-filter bg-white w-full sm:w-9"
                                title="Filter List"
                            >
                                <Filter className="w-4 h-4" />
                            </Button>

                            {isFilterOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setIsFilterOpen(false)} />
                                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                                        <button
                                            onClick={() => { setFilterStatus('all'); setIsFilterOpen(false); dispatch(setCurrentAnswerSheetPage(1)); }}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${filterStatus === 'all' ? 'text-primary font-medium bg-primary/5' : 'text-gray-700'}`}
                                        >
                                            All Sheets
                                            {filterStatus === 'all' && <CheckCircle2 className="w-3.5 h-3.5" />}
                                        </button>
                                        <button
                                            onClick={() => { setFilterStatus('processing'); setIsFilterOpen(false); dispatch(setCurrentAnswerSheetPage(1)); }}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${filterStatus === 'processing' ? 'text-primary font-medium bg-primary/5' : 'text-gray-700'}`}
                                        >
                                            Processing
                                            {filterStatus === 'processing' && <CheckCircle2 className="w-3.5 h-3.5" />}
                                        </button>
                                        <button
                                            onClick={() => { setFilterStatus('completed'); setIsFilterOpen(false); dispatch(setCurrentAnswerSheetPage(1)); }}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${filterStatus === 'completed' ? 'text-primary font-medium bg-primary/5' : 'text-gray-700'}`}
                                        >
                                            Completed
                                            {filterStatus === 'completed' && <CheckCircle2 className="w-3.5 h-3.5" />}
                                        </button>
                                        <button
                                            onClick={() => { setFilterStatus('failed'); setIsFilterOpen(false); dispatch(setCurrentAnswerSheetPage(1)); }}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${filterStatus === 'failed' ? 'text-primary font-medium bg-primary/5' : 'text-gray-700'}`}
                                        >
                                            Failed
                                            {filterStatus === 'failed' && <CheckCircle2 className="w-3.5 h-3.5" />}
                                        </button>

                                        <div className="h-px bg-gray-100 my-1 mx-2" />

                                        <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                            Rows per page
                                        </div>
                                        {[10, 20, 50, 100].map(limit => (
                                            <button
                                                key={limit}
                                                onClick={() => {
                                                    setItemsPerPage(limit);
                                                    setIsFilterOpen(false);
                                                    dispatch(setCurrentAnswerSheetPage(1));
                                                }}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${itemsPerPage === limit ? 'text-primary font-medium bg-primary/5' : 'text-gray-700'}`}
                                            >
                                                {limit} Rows
                                                {itemsPerPage === limit && <CheckCircle2 className="w-3.5 h-3.5" />}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* List Table */}
            <div className="bg-white border min-h-[300px] border-border rounded-lg shadow-sm flex flex-col overflow-hidden">
                <div className="overflow-x-auto flex-1 flex flex-col">
                    <div className="min-w-0 flex-1 flex flex-col">
                        {/* Header */}
                        <div className="tour-list-table flex items-center px-3 md:px-6 py-3 bg-gray-50 border-b border-border text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider shrink-0">
                            <div className="w-[32px] md:w-[48px] flex justify-center shrink-0">
                                <button onClick={handleSelectAll} className="text-gray-500 hover:text-primary transition-colors">
                                    {isAllVisibleSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
                                </button>
                            </div>
                            <div className="flex-1 min-w-0 pl-1">File Name</div>
                            <div className="w-[64px] md:w-[140px] shrink-0 text-center">Status</div>
                            <div className="w-[52px] md:w-[120px] shrink-0 text-center">Score</div>
                            <div className="hidden sm:block sm:w-[100px] shrink-0 text-center">Percentage</div>
                            <div className="w-[32px] md:w-[120px] shrink-0 text-right"><span className="hidden md:inline">Actions</span></div>
                        </div>

                        {/* Rows */}
                        {loading ? (
                            <div className="flex-1 flex items-center justify-center p-12">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto">
                                {currentItems.length > 0 ? currentItems.map((sheet: AnswerSheet) => {
                                    const isCompleted = sheet.status === 'completed';
                                    const isSelected = selectedIds.includes(sheet.id);
                                    const isSheetProcessing = isProcessing(sheet.status);

                                    const renderStatusBadge = (status: string | null) => {
                                        switch (status) {
                                            case 'completed': return <span className="inline-flex items-center gap-1 px-1.5 md:px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-[10px] md:text-xs font-bold border border-green-100"><CheckCircle2 className="w-3 h-3" /> <span className="hidden xs:inline">Completed</span><span className="xs:hidden">Done</span></span>;
                                            case 'processing': case 'in-progress': return <span className="inline-flex items-center gap-1 px-1.5 md:px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-[10px] md:text-xs font-bold border border-orange-100"><Loader2 className="w-3 h-3 animate-spin" /> <span className="hidden xs:inline">Processing</span><span className="xs:hidden">Processing</span></span>;
                                            case 'failed': return <span className="inline-flex items-center gap-1 px-1.5 md:px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-[10px] md:text-xs font-bold border border-red-100"><X className="w-3 h-3" /> <span className="hidden xs:inline">Failed</span><span className="xs:hidden">Fail</span></span>;
                                            default: return <span className="inline-flex items-center gap-1 px-1.5 md:px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-[10px] md:text-xs font-bold border border-gray-200"><Clock className="w-3 h-3" /> Not Evaluated</span>;
                                        }
                                    };

                                    return (
                                        <div key={sheet.id} onClick={() => !isSheetProcessing && handleSelectOne(sheet.id)} className={`group flex items-center px-3 md:px-6 py-3 md:py-4 border-b border-border last:border-0 transition-colors ${isSheetProcessing ? "opacity-60 cursor-not-allowed bg-gray-50/50" : "cursor-pointer hover:bg-gray-50/80"} ${isSelected ? "bg-primary/5 hover:bg-primary/10" : ""}`}>
                                            <div className="w-[32px] md:w-[48px] flex justify-center shrink-0">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); !isSheetProcessing && handleSelectOne(sheet.id); }}
                                                    disabled={isSheetProcessing}
                                                    className={`transition-colors ${isSelected ? "text-primary" : "text-gray-400 hover:text-gray-600"} ${isSheetProcessing ? "cursor-not-allowed opacity-50" : ""}`}
                                                >
                                                    {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                </button>
                                            </div>
                                            <div className="flex-1 min-w-0 flex flex-col gap-0.5 pl-1 pr-2 overflow-hidden">
                                                <span
                                                    className="font-mono text-xs md:text-sm font-medium text-primary hover:text-primary/80 truncate cursor-pointer hover:underline"
                                                    title="View PDF"
                                                    onClick={(e) => handleViewPdf(e, sheet.s3Url)}
                                                >
                                                    {sheet.fileName}
                                                </span>
                                                <div className="sm:hidden flex items-center gap-2">
                                                    {sheet.percentage !== undefined && isCompleted && (
                                                        <span className="text-[10px] font-bold text-gray-500">{sheet.percentage?.toFixed(1)}%</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="w-[64px] md:w-[140px] shrink-0 flex justify-center">
                                                {renderStatusBadge(sheet.status)}
                                            </div>
                                            <div className="w-[52px] md:w-[120px] shrink-0 text-center font-bold text-gray-900 text-[11px] md:text-sm whitespace-nowrap">
                                                {!isCompleted ? "-" : `${sheet.score}/${sheet.maxScore}`}
                                            </div>
                                            <div className="hidden sm:block sm:w-[100px] shrink-0 text-center font-bold text-gray-700 text-xs md:text-sm">
                                                {!isCompleted ? "-" : `${sheet.percentage?.toFixed(1)}%`}
                                            </div>

                                            <div className="w-[32px] md:w-[120px] shrink-0 flex justify-end">
                                                {isCompleted ? (
                                                    <button onClick={(e) => { e.stopPropagation(); onViewDetails(sheet.id); }} title="View Details" className="text-[11px] md:text-sm font-bold text-primary hover:text-primary/80 transition-opacity flex items-center gap-1">
                                                        <span className="hidden md:inline">View Details</span> <ChevronRight className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <div className="w-4" />
                                                )}
                                            </div>
                                        </div>
                                    )
                                }) : <div className="flex flex-col items-center justify-center py-12 text-gray-400"><p>No answer sheets found.</p></div>}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between px-2 gap-4 sm:gap-0">
                <p className="text-xs text-gray-500">Showing <span className="font-bold text-gray-900">{Math.min(startIndex + 1, safeTotalCopies)}</span> - <span className="font-bold text-gray-900">{Math.min(startIndex + itemsPerPage, safeTotalCopies)}</span> of {safeTotalCopies}</p>
                <div className="flex items-center gap-2">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-2 bg-white border border-border rounded-lg text-gray-600 hover:text-primary hover:border-primary disabled:opacity-50 disabled:hover:text-gray-600 transition-all"><ChevronLeft className="w-4 h-4" /></button>
                    <div className="flex items-center gap-1">
                        {(() => {
                            let startPage = 1;
                            let endPage = Math.min(totalPages, 5);

                            if (totalPages > 5) {
                                if (currentPage <= 3) {
                                    startPage = 1;
                                    endPage = 5;
                                } else if (currentPage >= totalPages - 2) {
                                    startPage = totalPages - 4;
                                    endPage = totalPages;
                                } else {
                                    startPage = currentPage - 2;
                                    endPage = currentPage + 2;
                                }
                            }

                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                    <button
                                        key={i}
                                        onClick={() => handlePageChange(i)}
                                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-all ${currentPage === i ? "bg-primary text-white shadow-md shadow-primary/20" : "bg-white border border-border text-gray-600 hover:bg-gray-50"}`}
                                    >
                                        {i}
                                    </button>
                                );
                            }
                            return pages;
                        })()}
                    </div>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 bg-white border border-border rounded-lg text-gray-600 hover:text-primary hover:border-primary disabled:opacity-50 disabled:hover:text-gray-600 transition-all"><ChevronRight className="w-4 h-4" /></button>
                </div>
            </div>

            {/* Add Sheet — PDF preview + upload (same flow as first-time upload) */}
            <PdfPreviewModal
                isOpen={isPreviewOpen}
                files={filesToPreview}
                subjectName={uploadSubjectName}
                subjectCode={uploadPaperCode}
                onBack={handleClosePreview}
                onClose={handleClosePreview}
                onUploaded={handleSheetsUploaded}
            />

            {/* PDF Viewer Modal */}
            {viewPdfUrl && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => setViewPdfUrl(null)}>
                    <div className="bg-white rounded-2xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="font-semibold text-lg">Answer Sheet Viewer</h3>
                            <button onClick={() => setViewPdfUrl(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>
                        <div className="flex-1 bg-gray-100 p-0">
                            <iframe src={viewPdfUrl} className="w-full h-full" title="PDF Viewer" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default AnswerSheetList;
